import { useGetCategoryById } from "modules/categories";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Button } from "ui";

import { useResolvedUsageGuide, useUsageGuideByScope } from "../../apis";
import { UsageGuideReadOnlyView } from "../../components";
import { UsageGuideEditor } from "../UsageGuideEditor";

import styles from "./ProductUsageGuide.module.scss";

const ProductUsageGuide = () => {
  const { t } = useTranslation();
  const { productId } = useParams();

  // Two reads on purpose. `resolved` is what the customer would get; the scoped
  // one answers the different question of whether this product has a guide of
  // its own. Only the second decides whether the editor is safe to show.
  const { resolvedGuide, isLoading: isLoadingResolved } = useResolvedUsageGuide(
    String(productId)
  );
  const { guide: ownGuide, isLoading: isLoadingOwn } = useUsageGuideByScope({
    productId
  });

  // Purely local until the admin saves. Creating the record on the button press
  // would be destructive: an empty product guide outranks the inherited one, so
  // a stray click would strip the category's content from this product — for
  // the assistant too — before anything was written to replace it.
  const [isDraftingOwnGuide, setIsDraftingOwnGuide] = useState(false);

  useEffect(() => {
    setIsDraftingOwnGuide(false);
  }, [productId]);

  const { category } = useGetCategoryById(
    resolvedGuide?.inherited
      ? (resolvedGuide.categoryId ?? undefined)
      : undefined
  );

  const isLoading = isLoadingResolved || isLoadingOwn;

  if (isLoading) {
    return (
      <div className={styles.productGuide_loader}>
        <ClipLoader size={30} color="#fb3973" />
      </div>
    );
  }

  if (ownGuide || isDraftingOwnGuide) {
    return (
      <div className={styles.productGuide}>
        <UsageGuideEditor
          scope={{ productId }}
          header={
            <div className={styles.productGuide_editorHeader}>
              <p className={styles.productGuide_scope}>
                {t("This product's own guide")}
              </p>

              {/* Nothing exists server-side yet, so the product is still
                  showing the inherited guide — say so, and let them back out. */}
              {!ownGuide && (
                <div className={styles.productGuide_draftNotice}>
                  <p className={styles.productGuide_bannerText}>
                    {t(
                      "Not saved yet — until you save, this product keeps showing the inherited guide."
                    )}
                  </p>

                  <Button
                    variant="default"
                    onClick={() => setIsDraftingOwnGuide(false)}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              )}
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.productGuide}>
      <div className={styles.productGuide_banner}>
        <div>
          <p className={styles.productGuide_bannerTitle}>
            {resolvedGuide ? (
              <>
                {t("Inherited from category")}{" "}
                <span className={styles.productGuide_bannerCategory}>
                  {category?.name ?? "…"}
                </span>
              </>
            ) : (
              t("No guide yet")
            )}
          </p>

          <p className={styles.productGuide_bannerText}>
            {resolvedGuide
              ? t(
                  "This content belongs to the category and is shared with every product under it. Editing it here is not possible on purpose."
                )
              : t(
                  "Neither this product nor its categories have a guide. Write one on the category if it applies to more than this product."
                )}
          </p>
        </div>

        <Button onClick={() => setIsDraftingOwnGuide(true)}>
          {t("Write a guide for this product")}
        </Button>
      </div>

      {resolvedGuide && <UsageGuideReadOnlyView guide={resolvedGuide} />}
    </div>
  );
};

export default ProductUsageGuide;
